import { appConfigStore } from 'Models/AppConfigStore';
import { PassengerTypeKey } from 'Models/Enums';
import passengerTypeStore from 'Models/PassengerTypeStore';
import * as React from 'react';
import { formatStandardDate } from 'Util/_HumanWritten/DateUtils';
import { isNotNullOrUndefined, isNullOrUndefined, stringNotEmpty } from 'Util/TypeGuards';
import { PassengerDetail } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/PassengerDetail';

export interface PassengerInfoBreakdownProps {
	passengers?: PassengerDetail[];
	showMore: boolean;
	showPassengerType: boolean;
	/**
	 * Whether this leg's vessel has seats at all, from the booked summary's `tripHasSeats`.
	 *
	 * <p>Undefined where the payload did not compute it, which keeps every existing caller behaving
	 * as it did. False is the only value that changes anything: it stops the rows promising a seat at
	 * check-in on a vessel that sells unreserved seating.</p>
	 */
	tripHasSeats?: boolean;
}

export const hasMoreInfo = (passenger: PassengerDetail, userPhoneEnabled: boolean, userPhoneRequired: boolean) => {
	return (
		isNotNullOrUndefined(passenger.age) ||
		stringNotEmpty(passenger.email) ||
		(userPhoneEnabled && userPhoneRequired && stringNotEmpty(passenger.phone)) ||
		isNotNullOrUndefined(passenger.dateOfBirth) ||
		stringNotEmpty(passenger.address) ||
		stringNotEmpty(passenger.gender)
	);
};

function getInlineDetails(passenger: PassengerDetail, userPhoneEnabled: boolean): string[] {
	const parts: string[] = [];
	if (isNotNullOrUndefined(passenger.age)) {
		parts.push(`${passenger.age}`);
	}
	if (isNotNullOrUndefined(passenger.dateOfBirth)) {
		const formatted = formatStandardDate(passenger.dateOfBirth as Date);
		if (formatted) {
			parts.push(formatted);
		}
	}
	if (stringNotEmpty(passenger.gender)) {
		parts.push(passenger.gender);
	}
	if (stringNotEmpty(passenger.email)) {
		parts.push(passenger.email);
	}
	if (userPhoneEnabled && stringNotEmpty(passenger.phone)) {
		parts.push(passenger.phone);
	}
	if (stringNotEmpty(passenger.address)) {
		parts.push(passenger.address);
	}
	return parts;
}

/**
 * The passenger's class and seat, as one chip — "Business · 12A", or just one of them.
 *
 * Both are independently optional: a ferry can have classes with no seats (unreserved seating), or
 * seats with no classes. Empty when neither applies, which is every booking on a ferry that has
 * not configured either.
 */
/**
 * The seat split from the deck it is on.
 *
 * <p>`seatLabel` arrives as one string — "Upper deck · 12A", or a bare "12A" on a vessel with one
 * deck (see `SeatLabels.WithDeck` serverside). The fare-type layout sets the seat in its own column,
 * so the two halves are separated here rather than the label being rebuilt from parts the client
 * does not have.</p>
 */
export function splitSeatLabel(seatLabel: string | undefined | null): { deck?: string; seat?: string } {
	if (!stringNotEmpty(seatLabel)) {
		return {};
	}
	const parts = (seatLabel as string).split(' · ').map(part => part.trim()).filter(stringNotEmpty);
	// One part is the seat itself — a vessel with a single deck names no deck.
	return parts.length > 1
		? { deck: parts.slice(0, -1).join(' · '), seat: parts[parts.length - 1] }
		: { seat: parts[0] };
}

export function seatingLabel(passenger: PassengerDetail): string {
	return [passenger.passengerClassName, passenger.seatLabel].filter(stringNotEmpty).join(' · ');
}

/**
 * Whether this passenger is still waiting for a seat.
 *
 * <p>A fare type and no seat, on a vessel that has seats. Deliberately not conditioned on whether
 * that fare type let the <i>booker</i> choose one: check-in seats everybody it can, so a passenger
 * on a class that picks no seats in the wizard is precisely the one who arrives unseated and gets
 * one at the gate.</p>
 *
 * <p>The vessel is the part that has to be asked rather than inferred. "Fare type, no seat" is also
 * true of every passenger on a ferry selling unreserved seating, where no seat is ever coming, and
 * promising one there is the one mistake worth a round trip to avoid.</p>
 *
 * <p>The wording matches the booking wizard's, which is where the booker was first told this would
 * happen — and, since check-in now actually assigns the seat, where it stops being a promise.</p>
 */
export function awaitingSeat(passenger: PassengerDetail, tripHasSeats?: boolean): boolean {
	// Only ever false on a vessel we have been told has no seats. Undefined is "not asked" — every
	// payload that does not compute it, where the old behaviour is the right one.
	if (tripHasSeats === false) {
		return false;
	}
	return stringNotEmpty(passenger.passengerClassName) && !stringNotEmpty(passenger.seatLabel);
}

/**
 * Whether any passenger on this booking has a class or a seat.
 *
 * <p>Exported because the callers gate on it too. The breakdown handles a site that collects no
 * passenger names — a deckhand still needs to see who is in 12A — but a caller that hides the whole
 * section behind `passengerInfoEnabled` takes that away again, which is what it used to do.</p>
 */
export function hasSeatingInfo(passengers: PassengerDetail[] | undefined | null): boolean {
	return (passengers ?? []).some(passenger => stringNotEmpty(seatingLabel(passenger)));
}

/**
 * How many passengers this breakdown will actually draw a row for.
 *
 * <p>Exported because a caller offering `Show more` has to count the same rows the list does, and
 * counting only the <b>named</b> ones did not: a party of four with one name but a seat each drew
 * three rows and offered no way to reach the fourth, because the list keeps a row for anyone with a
 * class or a seat while the button only knew about names.</p>
 */
export function countVisiblePassengerRows(passengers: PassengerDetail[] | undefined | null): number {
	return visiblePassengerRows(passengers).length;
}

/**
 * The passengers that get a row: a name where names are shown, or a class or seat either way.
 *
 * <p>The list cuts at `PASSENGER_ROWS_BEFORE_SHOW_MORE` of THESE, not of every passenger. Cutting by
 * position in the full list hid a named passenger who came after unnamed ones — five passengers with
 * only the first and fifth named drew one row, and `Show more`, counting two, never appeared.</p>
 */
function visiblePassengerRows(passengers: PassengerDetail[] | undefined | null): PassengerDetail[] {
	const { passengerInfoEnabled } = appConfigStore;
	return (passengers ?? []).filter(passenger => {
		const named =
			passengerInfoEnabled
			&& (stringNotEmpty(passenger.firstName) || stringNotEmpty(passenger.lastName));
		return named || stringNotEmpty(seatingLabel(passenger));
	});
}

/** How many rows the list shows before `Show more` is needed. */
export const PASSENGER_ROWS_BEFORE_SHOW_MORE = 3;

export function PassengerInfoBreakdown({
	passengers,
	showMore,
	showPassengerType,
	tripHasSeats,
}: PassengerInfoBreakdownProps) {
	const { userPhoneEnabled, passengerInfoEnabled } = appConfigStore;

	if (isNullOrUndefined(passengers) || passengers.length === 0) {
		return null;
	}

	// Seating is shown even when passenger info is not collected. A site that does not ask for names
	// still needs the deckhand to see who is in 12A — and without this the whole breakdown is
	// suppressed, so the seat would never appear at check-in.
	const anySeating = hasSeatingInfo(passengers);
	if (!passengerInfoEnabled && !anySeating) {
		return null;
	}

	const visible = visiblePassengerRows(passengers);
	const shown = showMore ? visible : visible.slice(0, PASSENGER_ROWS_BEFORE_SHOW_MORE);

	/*
		The fare-type layout, on sites that sell fare types only.

		Design 3a: the type, then the fare and deck, then the seat on its own right-aligned column, so
		a list of four reads down three columns instead of as four sentences. Gated rather than
		applied everywhere because a site without fare types has nothing for the middle column and
		would be left with a grid holding one value.

		Borderless. The ruled version of this laid a table into a page built out of spaced sections,
		and its rules sat directly under the section's own divider — two rules a few pixels apart
		saying different things. The columns do the aligning; the spacing does the separating.
	*/
	if (appConfigStore.passengerClassesEnabled) {
		return (
			<div className="passenger-info-list passenger-info-list--fares">
				{shown.map((passenger, index) => {
					const hasName =
						passengerInfoEnabled
						&& (stringNotEmpty(passenger.firstName) || stringNotEmpty(passenger.lastName));

					const { deck, seat } = splitSeatLabel(passenger.seatLabel);
					const name = `${passenger.firstName ?? ''} ${passenger.lastName ?? ''}`.trim();
					const pending = awaitingSeat(passenger, tripHasSeats);

					return (
						<div
							key={passenger.id ?? index}
							/*
								The type column is dropped rather than emptied where the caller is
								already grouped by type — the check-in panel lists each type with its
								own heading, so the column held nothing and left the fare floating in
								the middle of a narrow panel with the seat at the far edge.
							*/
							className={`passenger-fare-row${
								showPassengerType ? '' : ' passenger-fare-row--no-type'
							}`}
						>
							{showPassengerType && (
								<span className="passenger-fare-row__type">
									{passengerTypeStore.getSingularName(passenger.key as PassengerTypeKey)}
								</span>
							)}
							<span className="passenger-fare-row__detail">
								{hasName && <span className="passenger-fare-row__name">{name}</span>}
								<span className="passenger-fare-row__fare">
									{stringNotEmpty(passenger.passengerClassName)
										&& passenger.passengerClassName}
									{stringNotEmpty(passenger.passengerClassName) && stringNotEmpty(deck) && (
										<span className="passenger-fare-row__separator">·</span>
									)}
									{stringNotEmpty(deck) && deck}
								</span>
							</span>
							{/* The seat, or why there is not one yet. Never both — a pending seat has no
							    number to right-align, and an empty column beside the note reads as a
							    seat that failed to load. Both read as a sentence fragment rather than
							    a labelled value, so the column stays one voice however it is filled. */}
							{pending ? (
								<span className="passenger-fare-row__pending">Seat assigned at check-in</span>
							) : (
								stringNotEmpty(seat) && (
									<span className="passenger-fare-row__seat">Seat {seat}</span>
								)
							)}
						</div>
					);
				})}
			</div>
		);
	}

	return (
		<div className="passenger-info-list">
			{shown.map((passenger, index) => {
				const seating = seatingLabel(passenger);
				// Names, and everything else a site collects about a passenger, are shown only where
				// the site collects them. A booking can still carry a name a site has stopped asking
				// for — an older booking, or one taken over the phone — and showing it would put
				// personal detail on screen the operator has chosen not to gather.
				const showPersonalDetails = passengerInfoEnabled;
				const hasName =
					showPersonalDetails
					&& (stringNotEmpty(passenger.firstName) || stringNotEmpty(passenger.lastName));

				const typeLabel = showPassengerType
					? passengerTypeStore.getSingularName(passenger.key as PassengerTypeKey)
					: '';
				const inlineDetails = showPersonalDetails ? getInlineDetails(passenger, userPhoneEnabled) : [];

				return (
					<div key={passenger.id ?? index} className="passenger-info__container">
						<div className="passenger-info__row">
							<span className="passenger-info__name">
								{hasName ? `${passenger.firstName ?? ''} ${passenger.lastName ?? ''}`.trim() : ''}
								{typeLabel && <span className="passenger-info__type">{typeLabel}</span>}
								{stringNotEmpty(seating) && (
									<span className="passenger-info__seating">{seating}</span>
								)}
								{awaitingSeat(passenger, tripHasSeats) && (
									<span className="passenger-info__seating-pending">
										Seat assigned at check-in
									</span>
								)}
							</span>
						</div>
						{inlineDetails.length > 0 && (
							<div className="passenger-info__inline-details">
								{inlineDetails.map((part, i) => (
									<React.Fragment key={part}>
										{i > 0 && <span className="passenger-info__inline-separator">·</span>}
										<span className="passenger-info__inline-detail">{part}</span>
									</React.Fragment>
								))}
							</div>
						)}
					</div>
				);
			})}
		</div>
	);
}
